/* eslint-disable react/require-default-props */
import { postJSON, getCSRFHeaders, getJSON } from '@folklore/fetch';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import { uniqBy } from 'lodash';
import shuffle from 'lodash/shuffle';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';

import allItems from '../../lib/items';
import { createSteppedGradient } from '../../lib/utils';

import { useModals } from '../../contexts/ModalsContext';
import BurnFx from '../../sounds/crispy_fire.mp3';
import Music from '../../sounds/santa.mp3';
import AudioPlayer from '../partials/AudioPlayer';
import Fire from '../partials/Fire';
import Icon from '../partials/Icon';
import PageMeta from '../partials/PageMeta';
import Share from '../partials/Share';
import UploadForm from '../partials/UploadForm';

// import Urbania from '../../img/urbania.png';
import styles from '../../styles/pages/home.module.css';

const propTypes = {
    path: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    size: PropTypes.number,
};

function HomePage({ path, items: initialItems = allItems, size = 5 }) {
    const uuid = useMemo(() => uuidV4(), []);
    const fireRef = useRef(null);
    const fxAudio = useRef(new Audio(BurnFx));
    const lastItems = useRef([]);
    const { setModal } = useModals();

    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [speed, setSpeed] = useState(1);
    const [intensity, setIntensity] = useState(1);

    const [menu, setMenu] = useState(shuffle(initialItems).slice(0, size));
    const [items, setItems] = useState([]);
    const [firstPlayed, setFirstPlayed] = useState(false);
    const gradient = useMemo(
        () =>
            createSteppedGradient(
                [
                    { value: '#ffff00', time: 0 },
                    { value: '#ff0000', time: 1 },
                ],
                100,
            ),
        [],
    );

    const color = useMemo(() => {
        let grad = null;
        if (intensity >= 98) {
            grad = gradient[98];
        }
        if (intensity >= 1) {
            grad = gradient[intensity - 1];
        }
        grad = { value: '#ffff00' };
        return grad.value;
    }, [gradient, intensity]);

    const setData = useCallback(
        (data) => {
            const { items: newItems = [], count: newCount = 0 } = data || {};
            setItems(
                (newItems || []).map((it) => ({ ...it, created_at: parseISO(it.created_at) })),
            );
            setCount(parseInt(newCount, 10) || 0);
            const power = parseFloat(data.power, 10) || 0;
            setIntensity(Math.max(Math.round(power * 20), 1));
            setSpeed(Math.max(Math.round(power * 20), 1));

            const [firstItem = null] = newItems || [];
            const [lastFirstItem = null] = lastItems.current || [];
            if (firstItem !== null && lastFirstItem !== null && firstItem.id !== lastFirstItem.id) {
                if (fireRef.current !== null) {
                    fireRef.current.makeAnimatedReveal();
                }
            }

            lastItems.current = newItems;
        },
        [setItems, setCount, setIntensity, setSpeed],
    );

    const getItems = useCallback(() => {
        getJSON(`${path !== null ? path : ''}/api/items`).then(setData);
    }, [setData, path]);

    useEffect(() => getItems(), [getItems]);

    useEffect(() => {
        const id = setInterval(() => {
            getItems();
        }, 5000);
        return () => {
            clearInterval(id);
        };
    }, [getItems, path, count]);

    const triggerFx = useCallback(
        (explode = false) => {
            if (fireRef.current !== null) {
                fireRef.current.makeAnimatedReveal();
                const random = Math.random() > 0.9 && intensity > 100;
                if (explode || random) {
                    fireRef.current.makeExplosions();
                } else {
                    fxAudio.current.play();
                }
            }
        },
        [intensity],
    );

    const postItem = useCallback(
        (item) =>
            postJSON(
                `${path !== null ? path : ''}/api/add`,
                { item },
                { headers: getCSRFHeaders() },
            )
                .then(setData)
                .catch((error) => {
                    console.error(error);
                }),
        [setData, setLoading, path],
    );

    const onUpload = useCallback(
        ({ name, base64, size: imageSize = null }) => {
            const item = {
                id: uuidV4(),
                type: 'upload',
                label: name,
                image: base64,
                logo: null,
                ...imageSize,
            };
            setLoading(true);
            postItem({ ...item, user: uuid })
                .then(() => {
                    triggerFx(true);
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        },
        [postItem, uuid, setLoading],
    );

    const onClick = useCallback(
        (item) => {
            const { id: newItemId = null } = item || {};
            setLoading(true);
            postItem({ ...item, user: uuid })
                .then(() => {
                    if (!firstPlayed) {
                        setFirstPlayed(true);
                    }
                    triggerFx();
                    setTimeout(() => {
                        setLoading(false);
                        setMenu(
                            uniqBy(
                                menu
                                    .map((it) => {
                                        const { id = null } = it || {};
                                        if (id !== null && id === newItemId) {
                                            const menuIds = menu.map((jt) => jt.id);
                                            return (
                                                shuffle([...initialItems]).find(
                                                    (jt) =>
                                                        jt.id !== newItemId &&
                                                        !menuIds.includes(jt.id),
                                                ) || null
                                            );
                                        }
                                        return it;
                                    })
                                    .filter((it) => it !== null),
                                'id',
                            ),
                        );
                    }, 1000);
                })
                .catch(() => {
                    setLoading(false);
                });
        },
        [
            intensity,
            setIntensity,
            speed,
            setSpeed,
            menu,
            setMenu,
            postItem,
            uuid,
            firstPlayed,
            setFirstPlayed,
            triggerFx,
            setLoading,
            initialItems,
        ],
    );

    const onClickInfo = useCallback(() => {
        setModal({ type: 'info' });
    }, [setModal]);

    const onReady = useCallback((fire) => {
        fireRef.current = fire;
    }, []);

    const onMute = useCallback((muted = true) => {
        fxAudio.current.muted = muted;
    }, []);

    return (
        <div className={classNames([styles.container, { [styles.disabled]: loading }])}>
            <PageMeta title="Feu 2024, bonjour 2025 — URBANIA" />
            <div className={styles.count} style={{ color: `#${color.toString(16)}` }}>
                {count}
                <div className={styles.label}>
                    <span>Items</span>
                    <span>Brulés</span>
                </div>
            </div>
            <Share className={styles.share} onClickInfo={onClickInfo} />
            <AudioPlayer
                className={styles.audio}
                audio={Music}
                forcePlay={firstPlayed}
                onMute={onMute}
                controls
                loop
            />
            <div className={styles.menu}>
                {/* <ArrowButton className={classNames([styles.icon, styles.arrow, styles.top])} /> */}
                {menu.map((it) => (
                    <Icon
                        className={styles.icon}
                        key={`menuItem-${it.id}`}
                        item={it}
                        onClick={onClick}
                    />
                ))}
                <UploadForm className={styles.icon} key="upload" onUpload={onUpload} />
                {/* <ArrowButton className={classNames([styles.icon, styles.arrow, styles.bottom])} /> */}
            </div>
            <Fire
                className={styles.fire}
                items={items}
                count={count}
                config={{ speed, intensity }}
                user={uuid}
                onReady={onReady}
            />
        </div>
    );
}

HomePage.propTypes = propTypes;

export default HomePage;
